/**
 * Holding a map's link (docs/plans/2026-10-28-link-sharing.md, "Redesign":
 * the link is the access): open the map it names, checked, without becoming
 * a member; keep it in the vault (signed in) or for this page; follow the
 * map's key changes and role through the link; the sync socket's one-time
 * tickets (websocket.js) and the attachments' header. Apart from the owner's
 * side (link-sharing.js) so the sync bridge can use it (no import cycle).
 *
 * The link's secret (after '#') never reaches the server; the token derived
 * from it tells the server who holds the link. A kept map's vault entry has
 * `link: { id, secret, ownerKey }` (the vault is encrypted for the account),
 * the map key wrapped for the account's own key, and the key log state
 * verified through the link (`keyAuthority`).
 */
import * as MapKeyTrust from "../crypto/map-key-trust.js";
import { VaultStorage } from "../crypto/vault-storage.js";
import { Keypair } from "../crypto/keypair.js";
import { linkKeys, linkAddress, parseLinkAddress } from "../crypto/link-keys.js";
import { getServerOrigin } from "../storage/index.js";
import { decryptInviteTitle } from "../crypto/invite-title.js";
import { getCurrentUser } from "../auth/handlers.js";
import { emit, Events } from "../core/events.js";
import { t } from "../../shared/i18n/index.js";

const headers = { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" };

// The server's refusals, by code, in the person's language.
const REFUSALS = {
  link_gone: "share.link.error.gone",
  link_exists: "share.link.error.exists",
  link_rate_limited: "share.link.error.tooMany",
};

/** An Error with the server's refusal in the person's language (`code`, `status` kept). */
export async function refusal(response, fallbackKey) {
  const body = await response.json().catch(() => ({}));
  const error = new Error(REFUSALS[body.code] ? t(REFUSALS[body.code]) : body.error || t(fallbackKey));
  error.code = body.code || null;
  error.status = response.status;
  return error;
}

/**
 * Maps open by their link for this page only (not kept: not signed in, or
 * already in the vault otherwise): mapId -> { link, state, dek, role, gone }.
 * `link`: { id, secret (base64), ownerKey } as a kept map's entry has it.
 */
const pageMaps = new Map();
/** Each link's token (base64), derived once from its secret. */
const tokens = new Map();

function linkOf(mapId) {
  return VaultStorage.getDoc?.(mapId)?.link || pageMaps.get(mapId)?.link || null;
}

/** Whether this map is open by its link (kept, or for this page). */
export function isLinkMap(mapId) {
  return !!linkOf(mapId);
}

/** Whether the link this map was opened by no longer works. */
export function isGone(mapId) {
  return !!(VaultStorage.getDoc?.(mapId)?.linkGone || pageMaps.get(mapId)?.gone);
}

/** A map open for this page only: its key (a copy; the caller zeroes it), or null. */
export function pageMapKey(mapId) {
  return pageMaps.get(mapId)?.dek.slice() || null;
}

/**
 * A map open by its link: { role, gone (the link no longer works), kept (in
 * this account's maps, else for this page), title, address (the link, to
 * share further) }, or null.
 */
export function held(mapId) {
  const entry = VaultStorage.getDoc?.(mapId);
  const page = pageMaps.get(mapId);
  const link = entry?.link || page?.link;
  if (!link) return null;
  const secret = Keypair.fromBase64(link.secret);
  try {
    const origin = getServerOrigin?.() || globalThis.location?.origin || "";
    return { role: entry?.link ? entry.role : page.role, gone: isGone(mapId), kept: !!entry?.link,
      title: (entry?.link ? entry.title : page.title) || "", address: linkAddress(origin, link.id, secret, link.ownerKey) };
  } finally {
    secret.fill(0);
  }
}

/** Which verified key-log state a map open by its link is at (its id), or null. */
export function keyStamp(mapId) {
  const entry = VaultStorage.getDoc?.(mapId);
  return (entry?.link ? entry.keyAuthority?.id : pageMaps.get(mapId)?.state?.id) || null;
}

/** A map open by its link: the key it holds now (raw, a copy; the caller zeroes it), or null. */
export async function currentKey(mapId) {
  if (VaultStorage.getDoc?.(mapId)?.link) return VaultStorage.getDocDEK(mapId);
  return pageMapKey(mapId);
}

/** A map open for this page only: { role, gone }, or null. */
export function pageMap(mapId) {
  const page = pageMaps.get(mapId);
  return page ? { role: page.role, gone: page.gone } : null;
}

async function derived(link) {
  const secret = Keypair.fromBase64(link.secret);
  try {
    return await linkKeys(secret);
  } finally {
    secret.fill(0);
  }
}

async function tokenOf(link) {
  if (!tokens.has(link.id)) {
    const { keypair, token } = await derived(link);
    keypair.secretKey.fill(0);
    tokens.set(link.id, Keypair.toBase64(token));
    token.fill(0);
  }
  return tokens.get(link.id);
}

/**
 * The link stopped working (turned off, replaced, the map deleted): said
 * once; the map stays readable from this device's copy.
 */
async function stopped(mapId) {
  const entry = VaultStorage.getDoc?.(mapId);
  if (entry?.link && !entry.linkGone) await VaultStorage.setDocEntry({ ...entry, linkGone: true });
  const page = pageMaps.get(mapId);
  if (page) page.gone = true;
  emit(Events.SHARE_LINK_GONE, { docId: mapId });
}

async function roleNow(mapId, role) {
  const entry = VaultStorage.getDoc?.(mapId);
  if (entry?.link && entry.role !== role) await VaultStorage.setDocEntry({ ...entry, role });
  const page = pageMaps.get(mapId);
  if (page) page.role = role;
}

/**
 * A one-time ticket for a sync socket on a map open by its link
 * (websocket.js), with the role the link gives now (stored). Null when the
 * link no longer works (said once, `SHARE_LINK_GONE`).
 * @returns {Promise<{ticket: string, role: string}|null>}
 */
export async function ticket(mapId) {
  const link = linkOf(mapId);
  // Known gone: not asked again (each refusal counts against this address).
  if (!link || isGone(mapId)) return null;
  const response = await fetch(`/api/links/${link.id}/ticket`, {
    method: "POST", credentials: "include", headers, body: JSON.stringify({ token: await tokenOf(link) }),
  });
  if (response.status === 404) {
    await stopped(mapId);
    return null;
  }
  if (!response.ok) throw await refusal(response, "share.link.error.openFailed");
  const answer = await response.json();
  if (answer.doc_id !== mapId || !["viewer", "editor"].includes(answer.role)) throw new Error(t("share.link.error.openFailed"));
  await roleNow(mapId, answer.role);
  return { ticket: answer.ticket, role: answer.role };
}

/** The value of the attachments' link header for a map open by its link, or null. */
export async function linkHeader(mapId) {
  const link = linkOf(mapId);
  return link ? `${link.id}.${await tokenOf(link)}` : null;
}

/** The key log from `after` on, as the link: { doc_id, role, title, events, ... }, or null when the link no longer works. */
async function readLog(linkId, token, after) {
  const events = [];
  let opened;
  for (;;) {
    const response = await fetch(`/api/links/${linkId}/open`, {
      method: "POST", credentials: "include", headers, body: JSON.stringify({ token, after_epoch: after }),
    });
    if (response.status === 404) return null;
    if (!response.ok) throw await refusal(response, "share.link.error.openFailed");
    opened = await response.json();
    if (!Array.isArray(opened.events) || opened.events.length > 100) throw new Error(t("share.link.error.openFailed"));
    events.push(...opened.events);
    if (opened.events.length < 100) break;
    after = opened.events.at(-1).state.epoch;
  }
  return { ...opened, events };
}

async function wrapForSelf(dek) {
  const publicKey = VaultStorage.getIndex()?.keypair?.publicKey;
  if (!publicKey) throw new Error(t("share.link.error.noKey"));
  return Keypair.toBase64(await Keypair.wrapDEKAnonymous(dek, Keypair.fromBase64(publicKey)));
}

/**
 * A map open by its link, after a key change (or to check it): the key log
 * since the state this device verified, walked as the link. Stores what
 * changed; resolves with the new map key when it changed (raw, the caller
 * zeroes it), else null (also when the link no longer works).
 */
export async function refreshKey(mapId) {
  const link = linkOf(mapId);
  const entry = VaultStorage.getDoc?.(mapId);
  const page = pageMaps.get(mapId);
  const kept = !!entry?.link;
  const state = kept ? entry.keyAuthority : page?.state;
  if (!link || !state || isGone(mapId)) return null;
  const { keypair, token } = await derived(link);
  try {
    const opened = await readLog(link.id, Keypair.toBase64(token), state.epoch);
    if (!opened) {
      await stopped(mapId);
      return null;
    }
    if (opened.doc_id !== mapId) throw new Error(t("share.link.error.openFailed"));
    const current = kept ? await VaultStorage.getDocDEK(mapId) : page.dek.slice();
    let followed;
    try {
      followed = MapKeyTrust.followLinkChain(mapId, link.id, keypair, link.ownerKey, state, current, opened.events);
    } finally {
      current.fill(0);
    }
    if (kept) {
      const wrappedDEK = followed.dek ? await wrapForSelf(followed.dek) : entry.wrappedDEK;
      await VaultStorage.setDocEntry({ ...VaultStorage.getDoc(mapId), wrappedDEK, keyAuthority: followed.state, role: followed.role });
      if (followed.dek) VaultStorage.clearDEKCache(mapId);
    } else {
      page.state = followed.state;
      page.role = followed.role;
      if (followed.dek) {
        page.dek.fill(0);
        page.dek = followed.dek.slice();
      }
    }
    return followed.dek;
  } finally {
    keypair.secretKey.fill(0);
    token.fill(0);
  }
}

/**
 * Remove a map kept by its link from this account's maps (nothing changes on
 * the server: it was never a member), or forget one open for this page.
 */
export async function remove(mapId) {
  const page = pageMaps.get(mapId);
  if (page) {
    page.dek.fill(0);
    pageMaps.delete(mapId);
  }
  if (VaultStorage.getDoc?.(mapId)?.link) await VaultStorage.deleteDoc(mapId);
}

/**
 * Open a link: the map it opens, checked (its key log, from the link's
 * entry, anchored on the owner the link names). Resolves with { mapId, role,
 * title, ownerUsername, memberRole (a member: their role; they open their
 * map as such), keep(), keepForPage(), discard() }: `keep()` (signed in)
 * keeps it in their maps, marked "via link", while the link works;
 * `keepForPage()` for this page only (not signed in); both resolve with the
 * map's id. `discard()` forgets the keys.
 */
export async function open(text) {
  const parsed = parseLinkAddress(text);
  if (!parsed) throw new Error(t("share.link.error.invalid"));
  const { keypair, token: tokenBytes } = await linkKeys(parsed.secret);
  const token = Keypair.toBase64(tokenBytes);
  tokenBytes.fill(0);
  const link = { id: parsed.linkId, secret: Keypair.toBase64(parsed.secret), ownerKey: parsed.creatorPublicKey };
  parsed.secret.fill(0);
  let verified = null;
  const forget = () => {
    verified?.dek.fill(0);
    verified = null;
    keypair.secretKey.fill(0);
  };
  try {
    const opened = await readLog(parsed.linkId, token, -1);
    if (!opened) throw new Error(t("share.link.error.gone"));
    const mapId = opened.doc_id;
    if (opened.member_role) {
      forget();
      const own = async () => mapId;
      return { mapId, role: opened.member_role, title: VaultStorage.getDoc?.(mapId)?.title || "", ownerUsername: opened.owner_username || null,
        memberRole: opened.member_role, keep: own, keepForPage: own, discard: () => {} };
    }
    verified = MapKeyTrust.verifyLinkChain(mapId, parsed.linkId, keypair, parsed.creatorPublicKey, opened.events);
    tokens.set(link.id, token);
    let title = "";
    if (opened.encrypted_title) title = await decryptInviteTitle(verified.dek, mapId, opened.encrypted_title).catch(() => "");
    // The owner as the verified key log names them (the server's name only
    // when the log has none).
    const ownerUsername = verified.state.members.find((m) => m.role === "owner")?.username || opened.owner_username || null;
    const role = verified.role;
    const keepForPage = async () => {
      if (!verified) throw new Error(t("share.link.error.openFailed"));
      pageMaps.get(mapId)?.dek.fill(0);
      pageMaps.set(mapId, { link, state: verified.state, dek: verified.dek.slice(), role, gone: false, title });
      forget();
      emit(Events.SHARE_LINK_OPENED, { docId: mapId, role, kept: false });
      return mapId;
    };
    return {
      mapId, role, title, ownerUsername, memberRole: null,
      async keep() {
        try {
          const existing = VaultStorage.getDoc?.(mapId);
          // Already in the vault otherwise (a member before, removed): that
          // entry stays as it is; the map opens for this page.
          if (!getCurrentUser() || (existing && !existing.link)) return await keepForPage();
          const wrappedDEK = await wrapForSelf(verified.dek);
          await VaultStorage.setDocEntry({
            docId: mapId,
            title: title || existing?.title || t("share.invite.defaultTitle"),
            folderId: existing?.folderId ?? null,
            role,
            wrappedDEK,
            keyAuthority: verified.state,
            link,
            linkGone: false,
            ...(ownerUsername && { ownerUsername }),
          });
          VaultStorage.clearDEKCache(mapId);
          // Open for this page before (not signed in then): kept now.
          pageMaps.get(mapId)?.dek.fill(0);
          pageMaps.delete(mapId);
          // The owner, as the link vouches for them.
          MapKeyTrust.rememberIdentities(mapId, { ...verified.state, witnesses: verified.state.witnesses.filter((w) => w.userId === verified.state.ownerId) });
          emit(Events.SHARE_LINK_OPENED, { docId: mapId, role, kept: true });
          return mapId;
        } finally {
          forget();
        }
      },
      keepForPage,
      discard: forget,
    };
  } catch (error) {
    forget();
    throw error;
  }
}
